import React, { useEffect, useState } from "react";
import { useParams, useOutletContext, Link } from "react-router-dom";
import portalApi from "@/lib/portalApi";
import { toast } from "sonner";
import { ArrowLeft, FileText, Camera, DollarSign, MessageSquare, ClipboardCheck, FileSignature } from "lucide-react";

const currency = (n) => n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function PortalProjectDetail() {
    const { id } = useParams();
    const { me } = useOutletContext();
    const [proj, setProj] = useState(null);
    const [tab, setTab] = useState("overview");
    const [error, setError] = useState("");

    useEffect(() => {
        portalApi.get(`/portal/projects/${id}`)
            .then(({ data }) => setProj(data))
            .catch((err) => setError(err.response?.data?.detail || "Not found"));
    }, [id]);

    if (error) return (
        <div data-testid="portal-project-error">
            <Link to="/portal/projects" className="inline-flex items-center gap-1 text-xs text-muted-foreground mb-4"><ArrowLeft size={12} /> Back</Link>
            <div className="border border-border p-8 text-sm">{error}</div>
        </div>
    );
    if (!proj) return <div className="text-sm text-muted-foreground">Loading…</div>;

    const type = me?.portal_type;
    const availableTabs = [
        { key: "overview", label: "Overview", show: true },
        { key: "documents", label: "Documents", icon: FileText, show: true },
        { key: "photos", label: "Photos", icon: Camera, show: type !== "subcontractor" },
        { key: "financials", label: "Financials", icon: DollarSign, show: type !== "subcontractor" && (type !== "client" || me?.show_financials) },
        { key: "updates", label: "Updates", icon: FileSignature, show: type !== "subcontractor" },
        { key: "submittals", label: "Submittals", icon: ClipboardCheck, show: type === "subcontractor" },
        { key: "questions", label: "Questions", icon: MessageSquare, show: true },
    ].filter((t) => t.show);

    return (
        <div data-testid="portal-project-detail">
            <Link to="/portal/projects" className="inline-flex items-center gap-1 text-xs text-muted-foreground mb-4"><ArrowLeft size={12} /> All projects</Link>

            <div className="mb-4">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{proj.project_type?.replace(/_/g, " ")}</div>
                <h1 className="text-2xl tracking-tight mt-1" data-testid="portal-project-name">{proj.name}</h1>
                <div className="text-xs text-muted-foreground">{proj.address}, {proj.city}, {proj.state}</div>
                {proj.is_demo && (
                    <div className="mt-2 inline-block bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900 px-2 py-1 text-[10px] uppercase tracking-widest" data-testid={`demo-badge-${proj.id}`}>Demonstration data — for exploration only</div>
                )}
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="border border-border p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Phase</div><div className="text-sm mt-1">{(proj.current_phase_key || "—").replace(/_/g, " ")}</div></div>
                <div className="border border-border p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Progress</div><div className="text-sm mt-1">{Math.round(proj.completion_pct || 0)}%</div></div>
                <div className="border border-border p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Status</div><div className="text-sm mt-1 capitalize">{proj.status}</div></div>
            </div>

            <div className="border-b border-border flex gap-4 overflow-x-auto mb-4" data-testid="portal-tabs">
                {availableTabs.map((t) => (
                    <button key={t.key} onClick={() => setTab(t.key)} className={`py-2 text-xs uppercase tracking-widest whitespace-nowrap border-b-2 ${tab === t.key ? "border-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`portal-tab-${t.key}`}>{t.label}</button>
                ))}
            </div>

            {tab === "overview" && <OverviewTab proj={proj} />}
            {tab === "documents" && <DocumentsTab projectId={id} />}
            {tab === "photos" && <PhotosTab projectId={id} />}
            {tab === "financials" && <FinancialsTab projectId={id} me={me} />}
            {tab === "updates" && <UpdatesTab projectId={id} />}
            {tab === "submittals" && <SubmittalsTab projectId={id} />}
            {tab === "questions" && <QuestionsTab projectId={id} />}
        </div>
    );
}

function OverviewTab({ proj }) {
    return (
        <div className="space-y-4" data-testid="portal-tab-overview">
            <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Phase progression</div>
                <div className="space-y-1">
                    {(proj.phases || []).map((ph) => (
                        <div key={ph.id} className="flex items-center gap-3 text-sm">
                            <span className="w-6 h-6 border border-border flex items-center justify-center text-[10px]">{ph.sequence}</span>
                            <span className="flex-1">{ph.name}</span>
                            <span className="text-xs text-muted-foreground capitalize">{ph.status?.replace(/_/g, " ")}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function DocumentsTab({ projectId }) {
    const [docs, setDocs] = useState([]);
    useEffect(() => { portalApi.get(`/portal/projects/${projectId}/documents`).then(({ data }) => setDocs(data.items || [])); }, [projectId]);
    return (
        <div data-testid="portal-tab-documents">
            {docs.length === 0 ? <div className="text-sm text-muted-foreground">No documents shared yet.</div> :
                <div className="border border-border divide-y divide-border">
                    {docs.map((d) => (
                        <div key={d.id} className="p-3 flex items-center justify-between text-sm">
                            <div>
                                <div className="font-medium">{d.filename}</div>
                                <div className="text-xs text-muted-foreground">{d.category} · {(d.size_bytes || 0).toLocaleString()} bytes</div>
                            </div>
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/portal/documents/${d.id}/download`} onClick={async (e) => {
                                e.preventDefault();
                                try {
                                    const res = await portalApi.get(`/portal/documents/${d.id}/download`, { responseType: "blob" });
                                    const url = URL.createObjectURL(res.data);
                                    const a = document.createElement("a"); a.href = url; a.download = d.filename; a.click();
                                    URL.revokeObjectURL(url);
                                } catch { toast.error("Download failed"); }
                            }} className="text-xs underline" data-testid={`doc-download-${d.id}`}>Download</a>
                        </div>
                    ))}
                </div>
            }
        </div>
    );
}

function PhotosTab({ projectId }) {
    const [photos, setPhotos] = useState([]);
    useEffect(() => { portalApi.get(`/portal/projects/${projectId}/photos`).then(({ data }) => setPhotos(data.items || [])); }, [projectId]);
    return (
        <div data-testid="portal-tab-photos">
            {photos.length === 0 ? <div className="text-sm text-muted-foreground">No photos yet.</div> :
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {photos.map((p) => (
                        <div key={p.id} className="aspect-square bg-secondary border border-border flex items-center justify-center text-xs text-muted-foreground p-2 text-center">
                            {p.caption || "Photo"}
                        </div>
                    ))}
                </div>
            }
        </div>
    );
}

function FinancialsTab({ projectId, me }) {
    const [fin, setFin] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => { portalApi.get(`/portal/projects/${projectId}/financials`).then(({ data }) => setFin(data)).catch((e) => setErr(e.response?.data?.detail || "Unavailable")); }, [projectId]);
    if (err) return <div className="text-sm text-muted-foreground" data-testid="portal-tab-financials-error">{err}</div>;
    if (!fin) return <div className="text-sm text-muted-foreground">Loading…</div>;
    const b = fin.budget || {};
    return (
        <div data-testid="portal-tab-financials" className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
                <div className="border border-border p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Original</div><div className="text-sm mt-1">{currency(b.original)}</div></div>
                <div className="border border-border p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Committed</div><div className="text-sm mt-1">{currency(b.committed)}</div></div>
                <div className="border border-border p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Actual</div><div className="text-sm mt-1">{currency(b.actual)}</div></div>
            </div>
            {fin.my_contributions?.length > 0 && (
                <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">My contributions</div>
                    <div className="border border-border divide-y divide-border text-sm">
                        {fin.my_contributions.map((c) => (
                            <div key={c.id} className="p-3 flex justify-between">
                                <div><div className="capitalize">{c.contribution_type?.replace(/_/g, " ")}</div><div className="text-xs text-muted-foreground">{c.contribution_date?.slice(0, 10)}</div></div>
                                <div>{currency(c.amount)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {fin.my_distributions?.length > 0 && (
                <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Distributions received</div>
                    <div className="border border-border divide-y divide-border text-sm">
                        {fin.my_distributions.map((d) => (
                            <div key={d.id} className="p-3 flex justify-between">
                                <div><div className="capitalize">{d.distribution_type?.replace(/_/g, " ")}</div><div className="text-xs text-muted-foreground">{d.distribution_date?.slice(0, 10)}</div></div>
                                <div>{currency(d.amount)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function UpdatesTab({ projectId }) {
    const [ups, setUps] = useState([]);
    useEffect(() => { portalApi.get(`/portal/projects/${projectId}/investor-updates`).then(({ data }) => setUps(data.items || [])); }, [projectId]);
    return (
        <div data-testid="portal-tab-updates">
            {ups.length === 0 ? <div className="text-sm text-muted-foreground">No updates published yet.</div> :
                <div className="space-y-3">
                    {ups.map((u) => (
                        <div key={u.id} className="border border-border p-4">
                            <div className="text-xs text-muted-foreground">{u.published_at?.slice(0, 10)}</div>
                            <div className="text-base font-medium tracking-tight mt-1">{u.title}</div>
                            <div className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{u.body}</div>
                        </div>
                    ))}
                </div>
            }
        </div>
    );
}

function SubmittalsTab({ projectId }) {
    const [subs, setSubs] = useState([]);
    const [err, setErr] = useState("");
    useEffect(() => { portalApi.get(`/portal/projects/${projectId}/submittals`).then(({ data }) => setSubs(data.items || [])).catch((e) => setErr(e.response?.data?.detail || "Unavailable")); }, [projectId]);
    if (err) return <div className="text-sm text-muted-foreground">{err}</div>;
    return (
        <div data-testid="portal-tab-submittals">
            {subs.length === 0 ? <div className="text-sm text-muted-foreground">No submittals assigned to you.</div> :
                <div className="border border-border divide-y divide-border text-sm">
                    {subs.map((s) => (
                        <div key={s.id} className="p-3 flex justify-between">
                            <div><div>{s.spec_section}: {s.product_name}</div><div className="text-xs text-muted-foreground">{s.review_status}</div></div>
                        </div>
                    ))}
                </div>
            }
        </div>
    );
}

function QuestionsTab({ projectId }) {
    const [items, setItems] = useState([]);
    const [body, setBody] = useState("");
    const [busy, setBusy] = useState(false);

    const load = () => portalApi.get("/portal/questions").then(({ data }) => setItems((data.items || []).filter((q) => q.project_id === projectId)));
    useEffect(() => { load(); }, [projectId]); // eslint-disable-line

    const submit = async (e) => {
        e.preventDefault();
        if (!body.trim()) return;
        setBusy(true);
        try {
            await portalApi.post("/portal/questions", { project_id: projectId, body });
            setBody("");
            toast.success("Question submitted");
            load();
        } catch { toast.error("Failed to submit"); }
        setBusy(false);
    };

    return (
        <div data-testid="portal-tab-questions" className="space-y-4">
            <form onSubmit={submit} className="border border-border p-4 space-y-2">
                <label className="text-xs uppercase tracking-widest text-muted-foreground">Ask a question</label>
                <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} className="w-full border border-border bg-background px-3 py-2 text-sm" data-testid="portal-question-body" />
                <button disabled={busy || !body.trim()} className="bg-foreground text-background px-3 py-1.5 text-xs" data-testid="portal-question-submit">
                    {busy ? "Submitting…" : "Submit question"}
                </button>
            </form>

            <div className="space-y-2">
                {items.length === 0 ? <div className="text-sm text-muted-foreground">No questions yet.</div> :
                    items.map((q) => (
                        <div key={q.id} className="border border-border p-3 text-sm">
                            <div className="text-xs text-muted-foreground">{q.created_at?.slice(0, 10)} · {q.status}</div>
                            <div className="mt-1">{q.body}</div>
                            {q.response && (
                                <div className="mt-2 pl-3 border-l-2 border-border">
                                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Response</div>
                                    <div className="mt-1">{q.response}</div>
                                </div>
                            )}
                        </div>
                    ))
                }
            </div>
        </div>
    );
}
